import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WorkProjectsListSkeleton from './WorkProjectsListSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './WorkProjectsList.scss';

const generateSlug = (title) => {
    if (!title) return '';
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

const processWorkData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(item => {
        const titleText = typeof item.mainTitle === 'object' 
            ? item.mainTitle.en || Object.values(item.mainTitle)[0] 
            : item.mainTitle;
        
        const slug = generateSlug(titleText);
        
        return {
            ...item,
            button: {
                text: {
                    ua: 'Переглянути проект',
                    en: 'View Project',
                    ru: 'Посмотреть проект',
                    pl: 'Zobacz projekt',
                    de: 'Projekt ansehen',
                    es: 'Ver proyecto',
                },
                link: `/work/${slug}`,
            }
        };
    });
};

const WorkProjectsList = ({ onReady }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { 
        data: workJsonData, 
        loading: workLoading, 
        error: workError, 
        refetch 
    } = useCachedFetch('/data/locales/work.json');
    
    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [workData, setWorkData] = useState([]);

    const getLocalizedText = useCallback((text) => {
        if (!text) return '';
        if (typeof text === 'object') {
            return text[currentLang] || text.en || '';
        }
        return text;
    }, [currentLang]);

    const getWorkListItems = useCallback((workList) => {
        if (!workList) return [];
        
        if (workList.type === 'list' && workList.items) {
            return workList.items.map(item => getLocalizedText(item));
        }
        
        if (workList[currentLang]) {
            return workList[currentLang];
        }
        
        return workList.en || [];
    }, [currentLang, getLocalizedText]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (workLoading) return;

        if (workError) {
            console.error('Error loading work projects data:', workError);
            setHasError(true);
            setHasData(false);
            setIsLoading(false);
            return;
        }

        try {
            setHasError(false);
   
            const processedData = processWorkData(workJsonData?.WorkData || []);
            setWorkData(processedData);
            
            const hasValidData = processedData.length > 0 && processedData.every(project => 
                project.mainTitle && 
                project.bgImage && 
                project.projectImage && 
                project.workList
            );
            
            setHasData(hasValidData);
            if (!hasValidData) {
                setHasError(true);
                console.error('Invalid data structure in work projects');
            }
        } catch (error) {
            console.error('Error processing work projects data:', error);
            setHasError(true);
            setHasData(false);
        } finally {
            setIsLoading(false);
        }
    }, [workJsonData, workLoading, workError]);

    useEffect(() => {
        if (!onReady) return;

        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                onReady();
            });
        });

        return () => cancelAnimationFrame(id);
    }, [onReady]);

    const sortedWorkData = useMemo(() => {
        return [...workData].sort((a, b) => b.id - a.id);
    }, [workData]);

    if (isLoading) {
        return <WorkProjectsListSkeleton />;
    }

    if (hasError || !hasData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="project__items">
            {sortedWorkData.map((project, index) => {
                const isRight = index % 2 === 0;
                const positionClass = isRight ? 'right' : 'left';

                const projectSlug = generateSlug(getLocalizedText(project.mainTitle));

                const workListItems = getWorkListItems(project.workList);

                return (
                    <div
                        key={project.id}
                        className={`project__item ${positionClass}`}
                    >
                        <div
                            className="project__item__bg__image"
                            style={{
                                backgroundImage: `url(${IMAGES_BASE_URL}${getLocalizedText(project.bgImage)})`,
                            }}
                        >
                            <AnimatedOnScroll 
                                        animation="fade-down"
                                        duration={800}
                                        delay={0}
                                        className="project__item__image"
                                      >
                                <img
                                    src={`${IMAGES_BASE_URL}${getLocalizedText(project.projectImage)}`}
                                    alt={`${getLocalizedText(project.mainTitle)} ${positionClass}`}
                                    className="project__item__pic"
                                    loading="lazy"
                                />
                            </AnimatedOnScroll>
                        </div>

                        <div className="project__item__container">
                            <div className="project__item__content">
                                <AnimatedOnScroll animation="fade-up" delay={0}>
                                <h4 className="project__item__title">
                                    {getLocalizedText(project.mainTitle)}
                                </h4>
                                </AnimatedOnScroll>
                                <AnimatedOnScroll animation="fade-up" delay={100}>
                                <h5 className="project__item__subtitle">
                                    {getLocalizedText(project.subtitle)}
                                </h5>
                                </AnimatedOnScroll>
                                <ul className="project__list">
                                    <AnimatedOnScroll animation="fade-up" delay={100}>
                                    {workListItems.map((item, i) => (
                                        <li key={i} className="project__list__item">
                                            {item}
                                        </li>
                                    ))}
                                    </AnimatedOnScroll>
                                </ul>
                                <AnimatedOnScroll animation="fade-up" delay={100}>
                                <Link
                                    to={`/work/${projectSlug}`}
                                    className="project__item__button"
                                >
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    {getLocalizedText(project.button?.text)}
                                </Link>
                                </AnimatedOnScroll>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default React.memo(WorkProjectsList);
export { generateSlug };